import {
    cloneElement,
    useEffect,
    useRef,
    type KeyboardEvent,
    type ReactNode,
    type Ref,
} from "react";
import classNames from "classnames";
import { ArrowDownIcon } from "@icons";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { PickerButton } from "./PickerButton";
import { composeHandlers } from "@design-system/overlay/composeHandlers";
import { mergeRefs } from "@design-system/overlay/mergeRefs";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { resolveChevronMode, usePickerRoot } from "./PickerContext";
import { PickerTrigger } from "./PickerTrigger";
import {
    PickerTriggerClear,
    PickerTriggerClearSlot,
} from "./PickerTriggerClear";
import type { PickerForcedState, PickerTriggerElement } from "./Picker.types";
import styles from "./PickerElementTrigger.module.scss";

export interface PickerButtonTriggerProps {
    /** The `Button` element (variant, size, block, icons, disabled, handlers); omitted, primary md. */
    render?: PickerTriggerElement;
    /** Label override for the current selection (a `renderValue`-style node); omitted, the root's selected label. */
    value?: ReactNode;
    /** Shown while nothing is selected (default "Select"). */
    placeholder?: ReactNode;
    /** Hover tooltip around the button; kept on a disabled button. */
    tooltip?: ReactNode;
    /** `true` keeps the chevron, `false` drops it; unset follows the root's `chevron`. */
    showChevron?: boolean;
    /** Clear control beside the label; clearing commits `null` / `[]` without opening. */
    clearable?: boolean;
    className?: string;
    ref?: Ref<HTMLButtonElement>;
    /** Playground-only: paints a state statically. */
    "data-forced-state"?: PickerForcedState;
}

const defaultElement: PickerTriggerElement = (
    <PickerButton variant="primary" size="md" />
);

const hasOwnChildren = (children: ReactNode) =>
    children !== undefined && children !== null && children !== false;

// The box takes the free width so a block Button keeps its chevron at the
// edge; the BreakableText inside it must shrink with it.
const TriggerLabel = ({ label }: { label: ReactNode }) => (
    <span className={styles.buttonTriggerLabelBox}>
        {typeof label === "string" ? (
            <BreakableText
                text={label}
                showTooltip
                className={classNames(
                    styles.buttonTriggerLabel,
                    styles.buttonTriggerText,
                )}
            />
        ) : (
            <span className={styles.buttonTriggerLabel}>{label}</span>
        )}
    </span>
);

/**
 * `Picker.ButtonTrigger`: a `Button` element as the root's labelled trigger,
 * the one the data-driven root renders from its `trigger` prop and the one
 * composed roots place themselves. Base UI's trigger props are merged with
 * the element's (ref, handlers, className); the element shows `value`, else
 * the selected label, else `placeholder`, when it has no children of its own
 * (icon-only buttons keep their glyph), gains the trailing chevron unless it
 * brings a `rightIcon`, is icon-only or the chevron is off, and `clearable`
 * wraps it in a positioning span with a sibling clear control measured onto
 * the slot reserved in the Button's right slot. An icon-only element has no
 * room for that slot, so it ignores `clearable`. `tooltip` wraps the button.
 */
export const PickerButtonTrigger = ({
    render: element = defaultElement,
    value,
    placeholder = "Select",
    tooltip,
    showChevron,
    clearable = false,
    className,
    ref,
    "data-forced-state": forcedState,
}: PickerButtonTriggerProps) => {
    const root = usePickerRoot();
    const chevronMode = resolveChevronMode({ showChevron, root });
    const elementProps = element.props;
    const buttonRef = useRef<HTMLButtonElement>(null);
    const clearSlotRef = useRef<HTMLSpanElement>(null);
    // Escape blurs the button one frame later, after Base UI has returned
    // focus to it on close; the frame is dropped if the trigger unmounts first.
    const blurFrameRef = useRef<number | null>(null);
    useEffect(
        () => () => {
            if (blurFrameRef.current !== null) {
                cancelAnimationFrame(blurFrameRef.current);
            }
        },
        [],
    );

    const isIconOnly = elementProps.iconOnly === true;
    const isClearable = clearable && !isIconOnly;
    const hasOwnLabel = isIconOnly || hasOwnChildren(elementProps.children);
    const label =
        value ?? root.selectionNode ?? root.selectionLabel ?? placeholder;
    const children = hasOwnLabel ? (
        elementProps.children
    ) : (
        <TriggerLabel label={label} />
    );
    // A node label (a `renderValue` element) has no guaranteed text: name the
    // button after the selection instead.
    const accessibleName =
        elementProps["aria-label"] ??
        (hasOwnLabel || typeof label === "string"
            ? undefined
            : (root.selectionLabel ?? undefined));

    const hasOwnRightIcon = elementProps.rightIcon != null;
    const chevron =
        !isIconOnly && !hasOwnRightIcon && chevronMode !== "none" ? (
            <ArrowDownIcon
                className={styles.buttonTriggerIcon}
                data-picker-chevron=""
                aria-hidden
            />
        ) : undefined;
    const trailingIcon = hasOwnRightIcon ? elementProps.rightIcon : chevron;
    const rightIcon = isClearable ? (
        <span
            className={styles.buttonTriggerTrailing}
            data-picker-part="trailing"
        >
            <PickerTriggerClearSlot ref={clearSlotRef} />
            {trailingIcon}
        </span>
    ) : (
        trailingIcon
    );

    const buttonTrigger = (
        <PickerTrigger
            // Top level so `Tooltip` sees the disabled button and hovers a
            // wrapper instead; the rendered button is disabled either way.
            disabled={elementProps.disabled === true || root.isDisabled}
            render={(triggerProps) => {
                const overrides = {
                    ...triggerProps,
                    ref: mergeRefs([
                        triggerProps.ref,
                        ref,
                        elementProps.ref,
                        buttonRef,
                    ]),
                    type: elementProps.type ?? "button",
                    className: classNames(
                        triggerProps.className,
                        styles.buttonTrigger,
                        elementProps.className,
                        className,
                    ),
                    "aria-invalid": root.isInvalid || undefined,
                    "aria-label": accessibleName,
                    // Base UI sends these as explicit `undefined` (no `Field`
                    // around it) and would drop what a label-less field set.
                    "aria-labelledby":
                        elementProps["aria-labelledby"] ??
                        triggerProps["aria-labelledby"],
                    "aria-required":
                        elementProps["aria-required"] ??
                        triggerProps["aria-required"],
                    "data-picker-part": "button-trigger",
                    "data-forced-state": forcedState ?? root.forcedState,
                    "data-chevron":
                        chevron !== undefined && chevronMode === "hover"
                            ? "hover"
                            : undefined,
                    onClick: composeHandlers(
                        triggerProps.onClick,
                        elementProps.onClick,
                    ),
                    onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => {
                        composeHandlers(
                            triggerProps.onKeyDown,
                            elementProps.onKeyDown,
                        )(event);
                        if (event.key === "Escape") {
                            const { currentTarget } = event;
                            blurFrameRef.current = requestAnimationFrame(() => {
                                blurFrameRef.current = null;
                                currentTarget.blur();
                            });
                        }
                    },
                    onBlur: composeHandlers(
                        composeHandlers(
                            triggerProps.onBlur,
                            root.onTriggerBlur,
                        ),
                        elementProps.onBlur,
                    ),
                    children,
                };
                return isIconOnly
                    ? cloneElement(element, overrides)
                    : cloneElement(element, { ...overrides, rightIcon });
            }}
        />
    );
    const trigger = <Tooltip content={tooltip}>{buttonTrigger}</Tooltip>;

    if (!isClearable) {
        return trigger;
    }
    return (
        <span
            className={styles.buttonTriggerGroup}
            data-block={elementProps.block ? "" : undefined}
        >
            {trigger}
            <PickerTriggerClear slotRef={clearSlotRef} triggerRef={buttonRef} />
        </span>
    );
};
